import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import AddUser from './AddUser';
import UserRole from './UserRole';

function AdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [chatConversations, setChatConversations] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);

  // Fetch classes from Supabase
  const fetchClasses = async () => {
    try {
      setLoading(true);
      const { data: classesList, error } = await supabase
        .from('classes')
        .select('*')
        .eq('is_active', true);
      
      if (error) {
        console.error('Error fetching classes:', error);
        setError('Failed to load classes. Please try again.');
        return;
      }
      
      setClasses(classesList || []);
    } catch (error) {
      console.error('Error fetching classes:', error);
      setError('Failed to load classes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClasses();
  }, []);

  // Fetch chat conversations (groups with student-teacher chats)
  const fetchChatConversations = async () => {
    try {
      setChatLoading(true);
      // Fetch all chat groups that have both students and teachers
      const { data: groups, error: groupsError } = await supabase
        .from('chat_groups')
        .select('*')
        .order('created_at', { ascending: false });

      if (groupsError) throw groupsError;

      // For each group, get members and filter for student-teacher conversations
      const conversations = [];
      for (const group of groups || []) {
        const { data: members, error: membersError } = await supabase
          .from('chat_group_members')
          .select('*')
          .eq('group_id', group.id);

        if (membersError) continue;

        const hasStudent = members.some(m => m.user_type === 'student');
        const hasTeacher = members.some(m => m.user_type === 'teacher');

        if (hasStudent && hasTeacher) {
          // Get latest message
          const { data: latestMessage } = await supabase
            .from('chat_messages')
            .select('*')
            .eq('group_id', group.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .single();

          // Get student and teacher info
          const studentMember = members.find(m => m.user_type === 'student');
          const teacherMember = members.find(m => m.user_type === 'teacher');

          // Fetch student details
          let studentName = studentMember?.display_name || 'Student';
          if (studentMember?.user_id) {
            const { data: student } = await supabase
              .from('students')
              .select('first_name, last_name')
              .eq('id', studentMember.user_id)
              .single();
            if (student) {
              studentName = `${student.first_name} ${student.last_name}`;
            }
          }

          // Fetch teacher details
          let teacherName = teacherMember?.display_name || 'Teacher';
          if (teacherMember?.user_id) {
            const { data: teacher } = await supabase
              .from('teachers')
              .select('first_name, last_name')
              .eq('id', teacherMember.user_id)
              .single();
            if (teacher) {
              teacherName = `${teacher.first_name} ${teacher.last_name}`;
            }
          }

          conversations.push({
            groupId: group.id,
            groupName: group.name || `${studentName} & ${teacherName}`,
            studentId: studentMember?.user_id,
            studentName: studentName,
            teacherId: teacherMember?.user_id,
            teacherName: teacherName,
            lastMessage: latestMessage,
            lastMessageTime: latestMessage?.created_at
          });
        }
      }

      // Sort by last message time
      conversations.sort((a, b) => {
        if (!a.lastMessageTime) return 1;
        if (!b.lastMessageTime) return -1;
        return new Date(b.lastMessageTime) - new Date(a.lastMessageTime);
      });

      setChatConversations(conversations);
    } catch (err) {
      console.error('Error fetching chat conversations:', err);
      setError('Failed to load chat conversations.');
    } finally {
      setChatLoading(false);
    }
  };

  // Fetch messages for selected conversation
  const fetchConversationMessages = async (groupId) => {
    try {
      setMessagesLoading(true);
      const { data: messages, error: messagesError } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('group_id', groupId)
        .order('created_at', { ascending: true });

      if (messagesError) throw messagesError;

      // Enrich messages with sender names
      const enrichedMessages = await Promise.all(
        (messages || []).map(async (message) => {
          let senderName = message.sender_id;
          if (message.sender_type === 'student') {
            const { data: student } = await supabase
              .from('students')
              .select('first_name, last_name')
              .eq('id', message.sender_id)
              .single();
            if (student) {
              senderName = `${student.first_name} ${student.last_name}`;
            }
          } else if (message.sender_type === 'teacher') {
            const { data: teacher } = await supabase
              .from('teachers')
              .select('first_name, last_name')
              .eq('id', message.sender_id)
              .single();
            if (teacher) {
              senderName = `${teacher.first_name} ${teacher.last_name}`;
            }
          }
          return { ...message, senderName };
        })
      );

      setConversationMessages(enrichedMessages);
    } catch (err) {
      console.error('Error fetching conversation messages:', err);
      setError('Failed to load messages.');
    } finally {
      setMessagesLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'chat') {
      fetchChatConversations();
    }
  }, [activeTab]);

  useEffect(() => {
    if (selectedConversation) {
      fetchConversationMessages(selectedConversation.groupId);
    }
  }, [selectedConversation]);

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      localStorage.clear();
      navigate('/admin');
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.clear();
      navigate('/admin');
    }
  };

  return (
    <div className="admin-dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <h1>Admin Dashboard</h1>
          <div className="header-actions">
            <button onClick={handleLogout} className="logout-btn">
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="dashboard-main">
        <div className="tabs">
          <button 
            className={`tab ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            Dashboard
          </button>
          <button 
            className={`tab ${activeTab === 'classes' ? 'active' : ''}`}
            onClick={() => setActiveTab('classes')}
          >
            Classes
          </button>
          <button 
            className={`tab ${activeTab === 'chat' ? 'active' : ''}`}
            onClick={() => setActiveTab('chat')}
          >
            Chat
          </button>
          <button 
            className={`tab ${activeTab === 'add-user' ? 'active' : ''}`}
            onClick={() => setActiveTab('add-user')}
          >
            Add User
          </button>
          <button 
            className={`tab ${activeTab === 'user-role' ? 'active' : ''}`}
            onClick={() => setActiveTab('user-role')}
          >
            User Role
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        {activeTab === 'dashboard' && (
          <div className="dashboard-content">
            <h2>Welcome to Admin Dashboard</h2>
            <p>This is a simplified admin dashboard. Full functionality is being developed.</p>
            
            <div className="stats-grid">
              <div className="stat-card">
                <h3>Total Classes</h3>
                <p className="stat-number">{classes.length}</p>
              </div>
              <div className="stat-card">
                <h3>Active Classes</h3>
                <p className="stat-number">{classes.filter(c => c.is_active).length}</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'classes' && (
          <div className="classes-content">
            <h2>Classes</h2>
            {loading ? (
              <p>Loading classes...</p>
            ) : (
              <div className="classes-list">
                {classes.map((cls) => (
                  <div key={cls.id} className="class-card">
                    <h3>{cls.class_name}</h3>
                    <p>{cls.description || 'No description'}</p>
                    <p>Status: {cls.is_active ? 'Active' : 'Inactive'}</p>
                  </div>
                ))}
                {classes.length === 0 && (
                  <p>No classes found.</p>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'chat' && (
          <div className="chat-content" style={{ display: 'flex', gap: '20px', height: 'calc(100vh - 200px)' }}>
            {/* Conversations List */}
            <div style={{ width: '300px', background: 'white', borderRadius: '8px', padding: '20px', overflowY: 'auto', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <h2 style={{ marginTop: 0, marginBottom: '20px', color: '#2c3e50' }}>Conversations</h2>
              {chatLoading ? (
                <p>Loading conversations...</p>
              ) : chatConversations.length === 0 ? (
                <p style={{ color: '#2c3e50', textAlign: 'center', padding: '20px' }}>No conversations found.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {chatConversations.map((conv) => (
                    <div
                      key={conv.groupId}
                      onClick={() => setSelectedConversation(conv)}
                      style={{
                        padding: '15px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        background: selectedConversation?.groupId === conv.groupId ? '#E3F2FD' : '#F5F5F5',
                        border: selectedConversation?.groupId === conv.groupId ? '2px solid #2196F3' : '1px solid #E0E0E0',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        if (selectedConversation?.groupId !== conv.groupId) {
                          e.currentTarget.style.background = '#EEEEEE';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedConversation?.groupId !== conv.groupId) {
                          e.currentTarget.style.background = '#F5F5F5';
                        }
                      }}
                    >
                      <div style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '5px' }}>
                        {conv.studentName} ↔ {conv.teacherName}
                      </div>
                      {conv.lastMessage && (
                        <div style={{ fontSize: '14px', color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {conv.lastMessage.content}
                        </div>
                      )}
                      {conv.lastMessageTime && (
                        <div style={{ fontSize: '12px', color: '#999', marginTop: '5px' }}>
                          {new Date(conv.lastMessageTime).toLocaleString()}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Messages View */}
            <div style={{ flex: 1, background: 'white', borderRadius: '8px', padding: '20px', display: 'flex', flexDirection: 'column', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              {selectedConversation ? (
                <>
                  <div style={{ borderBottom: '1px solid #E0E0E0', paddingBottom: '15px', marginBottom: '20px' }}>
                    <h2 style={{ marginTop: 0, marginBottom: '5px', color: '#2c3e50' }}>
                      {selectedConversation.studentName} ↔ {selectedConversation.teacherName}
                    </h2>
                    <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>Conversation</p>
                  </div>
                  <div style={{ flex: 1, overflowY: 'auto', padding: '10px', background: '#F9F9F9', borderRadius: '8px', marginBottom: '20px' }}>
                    {messagesLoading ? (
                      <p style={{ textAlign: 'center', color: '#2c3e50' }}>Loading messages...</p>
                    ) : conversationMessages.length === 0 ? (
                      <p style={{ textAlign: 'center', color: '#2c3e50' }}>No messages in this conversation.</p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        {conversationMessages.map((message) => (
                          <div
                            key={message.id}
                            style={{
                              alignSelf: message.sender_type === 'student' ? 'flex-start' : 'flex-end',
                              maxWidth: '70%'
                            }}
                          >
                            <div
                              style={{
                                padding: '12px 16px',
                                borderRadius: '12px',
                                background: message.sender_type === 'student' ? '#E3F2FD' : '#C8E6C9',
                                color: '#2c3e50'
                              }}
                            >
                              <div style={{ fontWeight: '600', marginBottom: '5px', fontSize: '14px' }}>
                                {message.senderName} ({message.sender_type})
                              </div>
                              <div style={{ marginBottom: '5px' }}>{message.content}</div>
                              <div style={{ fontSize: '12px', color: '#666', textAlign: 'right' }}>
                                {new Date(message.created_at).toLocaleString()}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#999' }}>
                  <p>Select a conversation to view messages</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'add-user' && <AddUser />}
        {activeTab === 'user-role' && <UserRole />}
      </main>
    </div>
  );
}

export default AdminDashboard;